import { useEffect, useMemo, useState } from "react"
import { observer } from "mobx-react"
import { Alert, Button, Card, Checkbox, Input, Space, Spin, Typography } from "antd"
import { useStore } from "stores/utils/use-store"
import { notifications } from "notifications"
import type { VnRoomsConfigType } from "domains/managingJson/store/types"
import { PAY_RULES, PAY_RULE_KEYS, PayRule, VN_ROOMS_CONFIG, isPayRule } from "./constants"
import { isObject, vnRoomsProblems } from "./validate"

const { Title, Paragraph, Text } = Typography

const pretty = (data: unknown) => JSON.stringify(data, null, 2)

const parse = (text: string): { value: unknown } | { error: string } => {
  try {
    return { value: JSON.parse(text) }
  } catch (error) {
    return { error: `Invalid JSON: ${(error as Error).message}` }
  }
}

export const VnRoomsConfig = observer(() => {
  const { dataStore: { managingJsonStore } } = useStore()

  const [loading, setLoading] = useState(true)
  const [found, setFound] = useState(false)
  const [text, setText] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    (async() => {
      await managingJsonStore.getData()
      const item = managingJsonStore.itemTypes?.find(
        i => i.type === VN_ROOMS_CONFIG.type && i.name === VN_ROOMS_CONFIG.name,
      )
      setFound(item !== undefined)
      setText(item ? pretty(item.data) : "")
      setLoading(false)
    })()
  }, [managingJsonStore])

  const parsed = useMemo(() => parse(text), [text])
  const config = "value" in parsed && isObject(parsed.value) ? parsed.value as VnRoomsConfigType : null
  const problems = "value" in parsed ? vnRoomsProblems(parsed.value) : [parsed.error]
  const listedRules = config?.enabledPayRules
  const enabledRules: PayRule[] = Array.isArray(listedRules) ? listedRules.filter(isPayRule) : []

  const toggleRule = (rule: PayRule, on: boolean) => {
    if (!config) return
    const next = on ? [...enabledRules, rule] : enabledRules.filter(key => key !== rule)
    setText(pretty({ ...config, enabledPayRules: PAY_RULE_KEYS.filter(key => next.includes(key)) }))
  }

  const onSave = async() => {
    if (!config || problems.length > 0) return
    setSaving(true)
    managingJsonStore.funcAfterSuccess = async() => {
      notifications.success("VN Rooms config saved")
      await managingJsonStore.getData()
    }
    await managingJsonStore.update({ ...VN_ROOMS_CONFIG, data: config })
    setSaving(false)
  }

  if (loading) return <Spin />

  if (!found) {
    return (
      <Alert
        type="error"
        showIcon
        message="No vnRooms config found"
        description={
          "The vnRooms / vnRooms app config does not exist yet. "
          + "It holds the room timings game-service needs, so it is not created from here."
        }
      />
    )
  }

  return (
    <div style={{ maxWidth: 900 }}>
      <Title level={2}>VN Rooms</Title>
      <Paragraph type="secondary">
        The <code>vnRooms</code> app config: country allowlist, room timings, pay rules and their thresholds.
        Saving replaces the whole config and refreshes the cache, so clients and services pick it up without a rename.
      </Paragraph>

      <Card title="Pay rules" style={{ marginBottom: 16 }}>
        <Space direction="vertical">
          {PAY_RULES.map(rule => (
            <Checkbox
              key={rule.key}
              checked={enabledRules.includes(rule.key)}
              disabled={!config}
              onChange={event => toggleRule(rule.key, event.target.checked)}
            >
              <Text code>{rule.key}</Text> <Text type="secondary">{rule.description}</Text>
            </Checkbox>
          ))}
        </Space>
      </Card>

      <Card title="Full config (JSON)" style={{ marginBottom: 16 }}>
        <Input.TextArea
          aria-label="vnRooms JSON"
          autoSize={{ minRows: 16 }}
          status={config ? undefined : "error"}
          value={text}
          onChange={event => setText(event.target.value)}
          style={{ fontFamily: "monospace" }}
        />
      </Card>

      {problems.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Not saved until these are fixed"
          description={<ul style={{ margin: 0 }}>{problems.map(problem => <li key={problem}>{problem}</li>)}</ul>}
        />
      )}

      <Button type="primary" onClick={onSave} loading={saving} disabled={problems.length > 0}>
        Save
      </Button>
    </div>
  )
})
